import { Injectable } from '@angular/core';

import { ProductV2, ProductV2Variation } from '../../models/product-v2.model';

import { CartItem } from './cart.state';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface ValidationOptions {
  checkAvailability?: boolean;
  checkQuantity?: boolean;
  checkVariation?: boolean;
  checkPrice?: boolean;
  maxQuantity?: number;
}

@Injectable({
  providedIn: 'root'
})
export class CartValidationService {
  
  private readonly DEFAULT_MAX_QUANTITY = 99;
  private readonly DEFAULT_OPTIONS: ValidationOptions = {
    checkAvailability: true,
    checkQuantity: true,
    checkVariation: true,
    checkPrice: true,
    maxQuantity: this.DEFAULT_MAX_QUANTITY
  };


  validateCartItem(item: CartItem, options: ValidationOptions = {}): ValidationResult {
    const opts = { ...this.DEFAULT_OPTIONS, ...options };
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!item.product) {
      errors.push('Produto não fornecido');
      return { isValid: false, errors, warnings };
    }

    if (!item.product.uuid && !item.product.id) {
      errors.push('Produto sem identificador válido');
    }

    if (!item.product.name || item.product.name.trim() === '') {
      warnings.push('Produto sem nome');
    }

    if (opts.checkQuantity) {
      if (item.quantity <= 0) {
        errors.push('Quantidade deve ser maior que zero');
      } else if (item.quantity > (opts.maxQuantity || this.DEFAULT_MAX_QUANTITY)) {
        errors.push(`Quantidade não pode ser maior que ${opts.maxQuantity}`);
      }
    }

    if (opts.checkVariation) {
      if (item.product.variations && item.product.variations.length > 0) {
        if (!item.selectedVariation) {
          errors.push('Variação deve ser selecionada para este produto');
        } else {
          this.validateVariation(item.selectedVariation, errors, warnings);
        }
      }
    }

    if (opts.checkPrice) {
      this.validatePrice(item, errors, warnings);
    }

    if (item.addedAt && item.addedAt > Date.now()) {
      warnings.push('Data de adição inválida');
    }

    if (item.lastModified && item.lastModified > Date.now()) {
      warnings.push('Data de modificação inválida');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  validateCartItems(items: CartItem[], options: ValidationOptions = {}): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    let _validCount = 0;

    if (!Array.isArray(items)) {
      errors.push('Lista de itens inválida');
      return { isValid: false, errors, warnings };
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const result = this.validateCartItem(item, options);
      
      if (!result.isValid) {
        errors.push(`Item ${i + 1}: ${result.errors.join(', ')}`);
      } else {
        _validCount++;
      }
      
      if (result.warnings.length > 0) {
        warnings.push(`Item ${i + 1}: ${result.warnings.join(', ')}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  sanitizeCartItems(items: CartItem[], options: ValidationOptions = {}): CartItem[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.filter(item => {
      const result = this.validateCartItem(item, options);
      return result.isValid;
    });
  }

  validateProductForCart(
    product: ProductV2, 
    quantity: number, 
    selectedVariation?: ProductV2Variation
  ): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!product) {
      errors.push('Produto não fornecido');
      return { isValid: false, errors, warnings };
    }

    if ((product as { available?: boolean }).available === false) {
      errors.push('Produto não está disponível');
    }

    if (quantity <= 0) {
      errors.push('Quantidade deve ser maior que zero');
    } else if (quantity > this.DEFAULT_MAX_QUANTITY) {
      errors.push(`Quantidade não pode ser maior que ${this.DEFAULT_MAX_QUANTITY}`);
    }

    if (product.variations && product.variations.length > 0) {
      if (!selectedVariation) {
        errors.push('Variação deve ser selecionada para este produto');
      } else {
        this.validateVariation(selectedVariation, errors, warnings);
      }
    }

    const hasValidPrice = this.hasValidPrice(product, selectedVariation);
    if (!hasValidPrice) {
      warnings.push('Produto sem preço válido');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  validateCartForCheckout(items: CartItem[]): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!items || items.length === 0) {
      errors.push('Carrinho vazio');
      return { isValid: false, errors, warnings };
    }

    const validationResult = this.validateCartItems(items, {
      checkAvailability: true,
      checkQuantity: true,
      checkVariation: true,
      checkPrice: true
    });

    if (!validationResult.isValid) {
      errors.push(...validationResult.errors);
    }

    warnings.push(...validationResult.warnings);

    const itemIds = items.map(item => item.id);
    const uniqueIds = new Set(itemIds);
    if (itemIds.length !== uniqueIds.size) {
      errors.push('Carrinho contém itens duplicados');
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    if (totalQuantity > 100) {
      warnings.push('Quantidade total muito alta');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  private validateVariation(variation: ProductV2Variation, errors: string[], warnings: string[]): void {
    if (!variation.uuid) {
      errors.push('Variação sem identificador válido');
    }

    if (!variation.name || variation.name.trim() === '') {
      warnings.push('Variação sem nome');
    }

    if ((variation as { available?: boolean }).available === false) {
      errors.push('Variação não está disponível');
    }

    if (!this.hasValidPriceForVariation(variation)) {
      warnings.push('Variação sem preço válido');
    }
  }

  private validatePrice(item: CartItem, errors: string[], warnings: string[]): void {
    const hasValidPrice = this.hasValidPrice(item.product, item.selectedVariation);
    if (!hasValidPrice) {
      warnings.push('Item sem preço válido');
    }
  }

  private hasValidPrice(product: ProductV2, variation?: ProductV2Variation): boolean {
    if (variation) {
      return this.hasValidPriceForVariation(variation);
    }
    
    return Boolean((product.salesPrice && product.salesPrice > 0) || 
           (product.price && product.price > 0));
  }

  private hasValidPriceForVariation(variation: ProductV2Variation): boolean {
    return Boolean((variation.salesPrice && variation.salesPrice > 0) || 
           (variation.price && variation.price > 0));
  }

  getValidationSummary(items: CartItem[]): {
    totalItems: number;
    validItems: number;
    invalidItems: number;
    warningCount: number;
    errors: string[];
    warnings: string[];
  } {
    const validationResult = this.validateCartItems(items);
    const validItems = this.sanitizeCartItems(items).length;
    
    return {
      totalItems: items.length,
      validItems,
      invalidItems: items.length - validItems,
      warningCount: validationResult.warnings.length,
      errors: validationResult.errors,
      warnings: validationResult.warnings
    };
  }
}
